import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getDiffsonaPrompts, updateDiffsonaPrompts } from '../client/ApiClient';
import { DiffsonaFromDescriptionPresets, PromptChoice } from '../utils/diffsona-from-description';
import { DEMO_LS_KEY } from '../utils/post-auth';
import { femaleNameRandom } from '../utils/name';
import './InfoTooltip';

export type DiffsonaPrompts = {
  name: string;
  base_prompt?: string;
  age_prompt?: string;
  ethnicity_prompt?: string;
  hair_prompt?: string;
  face_prompt?: string;
  body_prompt?: string;
  bust_prompt?: string;
  waist_prompt?: string;
  hips_prompt?: string;
};

type EditableKey = 'base' | 'age' | 'ethnicity' | 'hair' | 'face' | 'body';

const FIELD_MAP: Record<EditableKey, keyof DiffsonaPrompts> = {
  base: 'base_prompt',
  age: 'age_prompt',
  ethnicity: 'ethnicity_prompt',
  hair: 'hair_prompt',
  face: 'face_prompt',
  body: 'body_prompt',
};

const FIELD_INFO: Record<EditableKey, { description: string; examples: string[] }> = {
  age: {
    description: "Character's age appearance",
    examples: ['21 year old woman', 'mid-30s man', 'teenage girl']
  },
  ethnicity: {
    description: "Character's ethnic appearance",
    examples: ['european', 'asian', 'african', 'latina']
  },
  hair: {
    description: 'Hair style and color',
    examples: ['shoulder-length blonde hair', 'short black hair', 'long curly red hair']
  },
  face: {
    description: 'Facial features and makeup',
    examples: ['soft features, light makeup', 'strong jawline', 'round face, freckles']
  },
  body: {
    description: 'Body type and build',
    examples: ['athletic build', 'slim figure', 'curvy', 'muscular']
  },
  base: {
    description: 'Additional character details',
    examples: ['casual style', 'professional attire', 'bohemian aesthetic']
  }
};

@customElement('current-diffsona')
export class CurrentDiffsona extends LitElement {
  createRenderRoot() { return this; }

  // You can set this attr, or we’ll infer from URL (/diffsona/:slug/*)
  @property({ type: String }) slug: string = '';

  // Demo mode disables API calls and uses provided prompts (plus local autosave)
  @property({ type: Boolean }) demo: boolean = false;

  // Fixed prompts from the caller (not reflected to attribute)
  @property({ attribute: false }) prompts: Partial<DiffsonaPrompts> | null = null;

  @state() private data: DiffsonaPrompts | null = null;
  @state() private loading = true;
  @state() private error: string | null = null;

  // Debounce timers per-field
  private timers = new Map<EditableKey, number>();

  connectedCallback(): void {
    super.connectedCallback();
    // If no slug is provided and not demo, infer from URL
    if (!this.demo && !this.slug) {
      const m = window.location.pathname.match(/\/diffsona\/([^/]+)/);
      if (m) this.slug = decodeURIComponent(m[1]);
    }
    this.load();
  }

  // React to prop changes (e.g., demo/prompts set after construction)
  protected updated(changed: Map<string, unknown>) {
    if (changed.has('demo') || changed.has('prompts') || changed.has('slug')) {
      this.load();
    }
  }

  private async load() {
    this.loading = true;
    this.error = null;

    // Demo path: hydrate from provided prompts, layered with any local edits
    if (this.demo) {
      if (this.prompts) {
        const fromProps: DiffsonaPrompts = {
          name: this.prompts.name || femaleNameRandom(),
          base_prompt: this.prompts.base_prompt,
          age_prompt: this.prompts.age_prompt,
          ethnicity_prompt: this.prompts.ethnicity_prompt,
          hair_prompt: this.prompts.hair_prompt,
          face_prompt: this.prompts.face_prompt,
          body_prompt: this.prompts.body_prompt,
        };
        this.data = fromProps;
        this.loading = false;

        return
      }

      const fromProps: DiffsonaPrompts = {
        name: 'Demo Diffsona',
        base_prompt: '',
        age_prompt: '21 year old woman',
        ethnicity_prompt: 'european',
        hair_prompt: 'shoulder-length hair',
        face_prompt: 'soft features, pretty face, light makeup',
        body_prompt: '',
      };

      try {
        const raw = localStorage.getItem(DEMO_LS_KEY);
        if (raw) {
          const overrides = JSON.parse(raw) as Partial<DiffsonaPrompts>;
          this.data = { ...fromProps, ...overrides, name: fromProps.name };
        } else {
          this.data = fromProps;
        }
      } catch {
        this.data = fromProps;
      } finally {
        this.loading = false;
      }
      return;
    }

    // Live path
    if (!this.slug) { this.loading = false; return; }
    try {
      this.data = await getDiffsonaPrompts(this.slug);
    } catch (e: any) {
      this.error = e?.message || 'Failed to load';
      this.data = null;
    } finally {
      this.loading = false;
    }
  }

  // === Inline editing support ===

  private getFieldValue(id: EditableKey): string {
    const key = FIELD_MAP[id];
    return (this.data?.[key] ?? '').trim();
  }

  private setFieldValue(id: EditableKey, value: string) {
    if (!this.data) return;
    const key = FIELD_MAP[id];
    this.data = { ...this.data, [key]: value };
    this.requestUpdate();

    // Emit event to notify parent components of the change (for demo mode)
    if (this.demo) {
      this.dispatchEvent(new CustomEvent('diffsona-prompts-changed', {
        detail: this.data,
        bubbles: true,
        composed: true,
      }));
    }
  }

  private scheduleSave(id: EditableKey) {
    // clear existing
    const existing = this.timers.get(id);
    if (existing) window.clearTimeout(existing);

    // debounce save
    const t = window.setTimeout(() => this.saveField(id), 600);
    this.timers.set(id, t as unknown as number);
  }

  private async saveField(id: EditableKey) {
    const value = this.getFieldValue(id);

    if (this.demo) {
      // Save to localStorage only
      try {
        const raw = localStorage.getItem(DEMO_LS_KEY);
        const current = raw ? (JSON.parse(raw) as Partial<DiffsonaPrompts>) : {};
        const key = FIELD_MAP[id];
        const next = { ...current, [key]: value, name: this.data?.name || femaleNameRandom() };
        localStorage.setItem(DEMO_LS_KEY, JSON.stringify(next));
      } catch { /* ignore */ }
      return;
    }

    if (!this.slug) return;
    try {
      // Only send the changed field to the API
      await updateDiffsonaPrompts(this.slug, {
        [id]: value,
      } as Record<string, string>);
    } catch (e) {
      // revert? keep optimistic; on error we could surface a toast if desired
      // console.warn('Autosave failed', e);
    }
  }

  private chip(label: string, id: EditableKey, values: PromptChoice[]) {
    const val = this.getFieldValue(id);
    const info = FIELD_INFO[id];

    return html`
      <div class="flex flex-col items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-slate-200 max-w-full mb-2">
        <div class="flex items-center justify-center gap-1.5 w-full">
          <span class="text-lg font-bold tracking-wide text-slate-400">${label}</span>
          <info-tooltip
            .description=${info.description}
            .examples=${info.examples}
          ></info-tooltip>
        </div>
        <prompt-input
          id=${id}
          .label=${id}
          .values=${values}
          ?allow-custom=${true}
          .initialValue=${val}
          @change=${(e: Event) => {
        const t = e.target as any;
        const v = (t?.value ?? '').toString();
        this.setFieldValue(id, v);
        this.scheduleSave(id);
      }}
        ></prompt-input>
      </div>
    `;
  }

  render() {
    const name = this.data?.name || '—';

    return html`
      <collapsible-section
        title="Appearance"
        icon="👤"
        description="Age, body, ethnicity"
        .open=${false}
        class="mb-2"
      >
        <header class="hidden mb-2 flex items-center justify-between gap-3">
          ${this.demo ? null : html`
            <a
              href=${`/diffsona/${encodeURIComponent(this.slug || '')}/edit`}
              class="shrink-0 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-sm"
            >Edit</a>
          `}
        </header>

        ${this.loading ? html`<div class="mt-2 h-3 w-40 rounded bg-slate-800 animate-pulse"></div>` : null}

        ${this.error ? null : html`
          <div class="flex gap-2 max-w-full overflow-x-auto">
            ${this.chip('Age', 'age', DiffsonaFromDescriptionPresets.age)}
            ${this.chip('Ethnicity', 'ethnicity', DiffsonaFromDescriptionPresets.ethnicity)}
            ${this.chip('Hair', 'hair', DiffsonaFromDescriptionPresets.hair)}
            ${this.chip('Face', 'face', DiffsonaFromDescriptionPresets.face)}
            ${this.chip('Body', 'body', DiffsonaFromDescriptionPresets.body)}
            ${this.chip('Extra', 'base', DiffsonaFromDescriptionPresets.base)}
          </div>
        `}
      </collapsible-section>
    `;
  }
}

